import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { createContext, use, useCallback, useMemo, useState } from 'react';

interface DropdownCoordinatorContextValue {
  openId: string | null;
  setOpenId: Dispatch<SetStateAction<string | null>>;
}

const DropdownCoordinatorContext = createContext<DropdownCoordinatorContextValue | null>(null);

export const HEADER_MOBILE_NAV_DROPDOWN_ID = 'header-mobile-nav';
export const SETTINGS_MOBILE_MENU_DROPDOWN_ID = 'settings-mobile-menu';

export function DropdownCoordinatorProvider({ children }: { children: ReactNode }) {
  const [openId, setOpenId] = useState<string | null>(null);
  const value = useMemo(() => ({ openId, setOpenId }), [openId]);

  return <DropdownCoordinatorContext.Provider value={value}>{children}</DropdownCoordinatorContext.Provider>;
}

interface CoordinatedDropdown {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

/**
 * Whether any dropdown sharing the nearest DropdownCoordinatorProvider is open.
 *
 * Lets an ancestor react to its dropdowns - e.g. suspend `overflow` scrolling,
 * which would otherwise clip the open panel. Always false without a provider.
 */
export function useIsAnyDropdownOpen(): boolean {
  const context = use(DropdownCoordinatorContext);

  return context?.openId != null;
}

/**
 * Tracks open/closed state for a dropdown identified by `id`. When rendered under a
 * DropdownCoordinatorProvider, only one id can be open at a time across all consumers
 * sharing that provider. Falls back to independent local state otherwise.
 */
export function useCoordinatedDropdown(id: string): CoordinatedDropdown {
  const context = use(DropdownCoordinatorContext);
  const [localOpen, setLocalOpen] = useState(false);
  const setOpenId = context?.setOpenId;

  const isOpen = context ? context.openId === id : localOpen;

  const open = useCallback(() => {
    if (setOpenId) {
      setOpenId(id);
      return;
    }
    setLocalOpen(true);
  }, [id, setOpenId]);

  const close = useCallback(() => {
    if (setOpenId) {
      setOpenId(prev => (prev === id ? null : prev));
      return;
    }
    setLocalOpen(false);
  }, [id, setOpenId]);

  const toggle = useCallback(() => {
    if (setOpenId) {
      setOpenId(prev => (prev === id ? null : id));
      return;
    }
    setLocalOpen(prev => !prev);
  }, [id, setOpenId]);

  return { isOpen, open, close, toggle };
}
